import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function Auth() {
  const nav = useNavigate();
  const loc = useLocation();
  const { login, register } = useApp();
  const params = new URLSearchParams(loc.search);
  const initialCode = (params.get('ref') || params.get('code') || '').toUpperCase();
  const [mode, setMode] = useState(loc.state?.mode || (initialCode ? 'signup' : 'signin'));
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [pw, setPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [code, setCode] = useState(initialCode);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !pw) { toast.error('Please fill in all fields'); return; }
    if (mode === 'signup' && !agree) { toast.error('Please accept the Terms, Privacy Policy and Disclaimer'); return; }
    setBusy(true);
    try {
      if (mode === 'signin') {
        const { cats } = await login(email, pw);
        toast.success('Welcome back!');
        nav(cats.length ? '/app/ask' : '/onboarding');
      } else {
        const { perk } = await register(email, name, pw, agree, code);
        if (perk === 'beta') toast.success('Beta code applied — 90 days of PRO unlocked! 🎉');
        else if (perk === 'referral') toast.success('Referral applied — enjoy 14 days of PRO free! 🎉');
        else toast.success('Account created!');
        nav('/onboarding');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background px-5 py-6">
      <button onClick={() => nav('/')} className="mb-6 inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-secondary">
        <ChevronLeft className="h-5 w-5" />
      </button>
      <div className="mx-auto max-w-md animate-fade-up">
        <h1 className="text-3xl font-extrabold tracking-tight">
          {mode === 'signin' ? 'Welcome back.' : 'Create your account.'}
        </h1>
        <p className="mt-2 text-muted-foreground">
          {mode === 'signin' ? 'Sign in to keep caring for your cat.' : 'Join Purrflect and give your cat the best care.'}
        </p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          {mode === 'signup' && (
            <div>
              <Label className="text-sm font-semibold">Your name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane" className="mt-1.5 h-12 rounded-xl bg-white" />
            </div>
          )}
          <div>
            <Label className="text-sm font-semibold">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5 h-12 rounded-xl bg-white" />
          </div>
          <div>
            <Label className="text-sm font-semibold">Password</Label>
            <div className="relative mt-1.5">
              <Input type={showPw ? 'text' : 'password'} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Your password" data-testid="auth-password" className="h-12 rounded-xl bg-white pr-11" />
              <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="toggle-password" aria-label={showPw ? 'Hide password' : 'Show password'} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {mode === 'signin' && (
              <button type="button" onClick={() => nav('/forgot-password')} data-testid="forgot-password-link" className="mt-2 text-sm font-semibold text-[hsl(var(--coral))] hover:underline">Forgot password?</button>
            )}
          </div>
          {mode === 'signup' && (
            <div>
              <Label className="text-sm font-semibold">Invite / beta code <span className="font-normal text-muted-foreground">(optional)</span></Label>
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. PDXCAT" data-testid="auth-code-input" className="mt-1.5 h-12 rounded-xl bg-white uppercase tracking-wide" />
              {code && <p className="mt-1 text-xs text-[hsl(var(--coral))]">A valid code unlocks free Purrflect PRO 🎉</p>}
            </div>
          )}
          <button type="submit" disabled={busy} className="coral-btn flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-base font-bold text-white disabled:opacity-70">
            {busy && <Loader2 className="h-5 w-5 animate-spin" />}{mode === 'signin' ? 'Sign in' : 'Create account'}
          </button>
          {mode === 'signup' && (
            <label className="mt-1 flex items-start gap-2.5 text-sm text-muted-foreground">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[hsl(var(--coral))]" />
              <span>
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="font-semibold text-[hsl(var(--coral))] underline underline-offset-2">Terms</a>,{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="font-semibold text-[hsl(var(--coral))] underline underline-offset-2">Privacy Policy</a>{' '}and{' '}
                <a href="/disclaimer" target="_blank" rel="noopener noreferrer" className="font-semibold text-[hsl(var(--coral))] underline underline-offset-2">Medical Disclaimer</a>.
              </span>
            </label>
          )}
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          {mode === 'signin' ? "Don't have an account?" : 'Already have an account?'}{' '}
          <button onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')} className="font-bold text-[hsl(var(--coral))]">
            {mode === 'signin' ? 'Sign up' : 'Sign in'}
          </button>
        </p>
      </div>
    </div>
  );
}
